import { useEffect, useRef, useState } from 'react';
import { api } from '@/api/user-dashboard/api';
import {
    DashboardWeather,
    WeatherListItem,
    WeatherHourItem,
} from '@/api/user-dashboard/type';

const WEATHER_ICON_BASE = '/images/weather/svg/';
const iconMap: Record<string, string> = {
    맑음: 'clear-day.svg',
    구름많음: 'partly-cloudy-day.svg',
    구름조금: 'partly-cloudy-day.svg',
    흐림: 'overcast.svg',
    비: 'rain.svg',
    눈: 'snow.svg',
    소나기: 'thunderstorms.svg',
    '구름많고 비': 'partly-cloudy-day-rain.svg',
    '흐리고 비': 'overcast-rain.svg',
};
const getIcon = (text?: string) =>
    WEATHER_ICON_BASE +
    (text && iconMap[text] ? iconMap[text] : 'clear-day.svg');

const formatDate = (d?: string) => {
    if (!d) return '';
    const parts = d.split('-');
    if (parts.length < 3) return d;
    const [y, m, day] = parts;
    return `${y}년 ${m}월 ${day}일`;
};

const formatShortDate = (d?: string) => {
    if (!d) return '';
    const parts = d.split('-');
    if (parts.length < 3) return d;
    const [, mm, dd] = parts;
    return `${parseInt(mm)}/${parseInt(dd)}`;
};

// HHmm → HH시
const formatTime = (wTime?: string) => {
    if (!wTime) return '';
    const hh = wTime.length >= 4 ? wTime.slice(0, 2) : wTime;
    return `${parseInt(hh)}시`;
};

const getNearestHourIdx = (hourList: WeatherHourItem[]) => {
    if (!hourList?.length) return 0;
    const now = new Date();
    const nowVal = now.getHours() * 100 + now.getMinutes();
    let nearest = 0;
    let minDiff = Infinity;
    hourList.forEach((h, i) => {
        const t = parseInt(h.wTime ?? '0', 10);
        const diff = Math.abs(t - nowVal);
        if (diff < minDiff) {
            minDiff = diff;
            nearest = i;
        }
    });
    return nearest;
};

// ────────────────────────────────────────────────────────────
// 시간대별 예보
// ────────────────────────────────────────────────────────────
interface HourlyForecastCardProps {
    hourList: WeatherHourItem[];
}

const HourlyForecastCard = ({ hourList }: HourlyForecastCardProps) => {
    const activeIdx = getNearestHourIdx(hourList);
    const scrollRef = useRef<HTMLDivElement>(null);
    const activeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!scrollRef.current || !activeRef.current) return;

        const container = scrollRef.current;
        const activeEl = activeRef.current;

        // 활성 카드를 컨테이너 중앙으로 스크롤
        const containerCenter = container.offsetWidth / 2;
        const activeCenter = activeEl.offsetLeft + activeEl.offsetWidth / 2;
        container.scrollLeft = activeCenter - containerCenter;
    }, [hourList]);

    if (!hourList?.length) {
        return (
            <p className="text-muted fs-13 text-center py-3">
                시간대별 날씨 정보가 없습니다.
            </p>
        );
    }

    return (
        <div className="card custom-card mb-0 no-shadow">
            <div
                ref={scrollRef}
                className="card-body"
                style={
                    {
                        overflowX: 'auto',
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'var(--primary-color) #eef0f5',
                    } as React.CSSProperties
                }
            >
                <div
                    className="d-flex"
                    style={
                        {
                            flexWrap: 'nowrap',
                            minWidth: `${hourList.length * 52 + (hourList.length - 1) * 6}px`,
                            gap: 6,
                        } as React.CSSProperties
                    }
                >
                    {hourList.map((h, idx) => {
                        const isActive = idx === activeIdx;
                        return (
                            <div
                                key={idx}
                                ref={isActive ? activeRef : null} // 활성 카드에만 ref 부착
                                className="rounded-3 d-flex flex-column align-items-center flex-shrink-0"
                                style={{
                                    flex: '1 1 0',
                                    minWidth: 52,
                                    padding: '10px 4px',
                                    background: isActive
                                        ? 'var(--primary-color)'
                                        : '#f8f9fc',
                                    border: isActive
                                        ? 'none'
                                        : '1px solid var(--default-border)',
                                }}
                            >
                                <p
                                    className="mb-1"
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: isActive
                                            ? 'rgba(255,255,255,0.85)'
                                            : 'var(--text-muted)',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {formatTime(h.wTime)}
                                </p>
                                <img
                                    src={getIcon(h.sky)}
                                    alt={h.sky ?? ''}
                                    style={{
                                        width: 28,
                                        height: 28,
                                        margin: '4px 0',
                                    }}
                                />
                                <p
                                    className="mb-1 fw-semibold"
                                    style={{
                                        fontSize: 14,
                                        color: isActive
                                            ? '#fff'
                                            : 'var(--default-text-color)',
                                    }}
                                >
                                    {h.tmp ?? '-'}°
                                </p>
                                <p
                                    className="mb-0"
                                    style={{
                                        fontSize: 10,
                                        color: isActive
                                            ? 'rgba(255,255,255,0.75)'
                                            : 'var(--text-muted)',
                                    }}
                                >
                                    <i className="ri-drop-line"></i>{' '}
                                    {h.pop ?? 0}%
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ────────────────────────────────────────────────────────────
// 오늘 날씨 카드
// ────────────────────────────────────────────────────────────
interface TodayWeatherCardProps {
    data: DashboardWeather;
}

const TodayWeatherCard = ({ data }: TodayWeatherCardProps) => {
    const today = data.list?.[0];

    const details = [
        {
            icon: 'ri-drop-line',
            label: '강수확률',
            value: today
                ? `오전 ${today.rainRateAm ?? 0}% / 오후 ${today.rainRatePm ?? 0}%`
                : '-',
        },
        {
            icon: 'ri-drizzle-line',
            label: '강수량',
            value: today ? `${today.wthRain ?? 0}mm` : '-',
        },
        {
            icon: 'ri-water-percent-line',
            label: '습도',
            value: today ? `${today.humidity ?? 0}%` : '-',
        },
        {
            icon: 'ri-sun-line',
            label: '일사량',
            value: today ? `${today.wthSrad ?? 0} MJ/m²` : '-',
        },
    ];

    return (
        <div className="card custom-card" style={{ minHeight: '402px' }}>
            <div className="card-header justify-content-between">
                <div>
                    <p className="mb-0 fw-semibold fs-14">
                        {formatDate(data.nowDate)}
                    </p>
                    <p className="mb-0 text-muted fs-12">
                        {data.yoil ?? ''}요일&nbsp;·&nbsp;음력{' '}
                        {data.nowLunaDate ?? ''}
                    </p>
                    <p className="mb-0 text-muted fs-12">
                        <i className="ri-map-pin-line me-1"></i>
                        {data.address ?? ''}
                    </p>
                </div>
                <span className="badge bg-success-transparent text-success rounded-pill fs-11 align-self-start">
                    <i className="ri-refresh-line me-1"></i>
                    {data.updateDate ?? ''}
                </span>
            </div>

            <div className="card-body d-flex flex-column gap-3">
                {today ? (
                    <div className="d-flex align-items-center gap-5 flex-wrap">
                        {/* 오전/오후 아이콘 */}
                        <div className="d-flex align-items-center gap-1">
                            <div className="text-center">
                                <img
                                    src={getIcon(today.weatherAm)}
                                    alt={today.weatherAm ?? ''}
                                    style={{ width: 70 }}
                                />
                                <p
                                    className="mb-0 text-muted"
                                    style={{ fontSize: 10 }}
                                >
                                    오전
                                </p>
                                <p
                                    className="mb-0 text-muted"
                                    style={{ fontSize: 10 }}
                                >
                                    {today.weatherAm ?? ''}
                                </p>
                            </div>
                            <span
                                className="text-muted mx-1"
                                style={{ fontSize: 18 }}
                            >
                                /
                            </span>
                            <div className="text-center">
                                <img
                                    src={getIcon(today.weatherPm)}
                                    alt={today.weatherPm ?? ''}
                                    style={{ width: 70 }}
                                />
                                <p
                                    className="mb-0 text-muted"
                                    style={{ fontSize: 10 }}
                                >
                                    오후
                                </p>
                                <p
                                    className="mb-0 text-muted"
                                    style={{ fontSize: 10 }}
                                >
                                    {today.weatherPm ?? ''}
                                </p>
                            </div>
                        </div>

                        {/* 온도 */}
                        <div>
                            <p
                                className="mb-0 fw-bold lh-1"
                                style={{ fontSize: 52 }}
                            >
                                {today.maxTemp ?? '-'}°
                            </p>
                            <p className="mb-0 mt-1" style={{ fontSize: 12 }}>
                                <span className="text-danger me-1">
                                    ↑ {today.maxTemp ?? '-'}°
                                </span>
                                <span className="text-primary">
                                    ↓ {today.minTemp ?? '-'}°
                                </span>
                            </p>
                        </div>

                        {/* 상세정보 */}
                        <div>
                            <div className="row g-2">
                                {details.map((item, i) => (
                                    <div className="col-6" key={i}>
                                        <div className="d-flex align-items-center gap-2">
                                            <span
                                                className="d-flex align-items-center justify-content-center rounded-2 bg-light flex-shrink-0"
                                                style={{
                                                    width: 30,
                                                    height: 30,
                                                }}
                                            >
                                                <i
                                                    className={`${item.icon} text-muted fs-15`}
                                                ></i>
                                            </span>
                                            <div>
                                                <p
                                                    className="mb-0 text-muted lh-1"
                                                    style={{ fontSize: 10 }}
                                                >
                                                    {item.label}
                                                </p>
                                                <p
                                                    className="mb-0 fw-medium lh-1 mt-1"
                                                    style={{ fontSize: 12 }}
                                                >
                                                    {item.value}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-muted fs-13 text-center py-3">
                        오늘 날씨 정보가 없습니다.
                    </p>
                )}

                <HourlyForecastCard hourList={data.hourList ?? []} />
            </div>
        </div>
    );
};

// ────────────────────────────────────────────────────────────
// 주간 예보 카드
// ────────────────────────────────────────────────────────────
interface WeeklyForecastCardProps {
    list: WeatherListItem[];
    nowDate?: string;
}

const WeeklyForecastCard = ({ list, nowDate }: WeeklyForecastCardProps) => {
    if (!list?.length) {
        return (
            <div className="card custom-card">
                <div className="card-header">
                    <div className="card-title">
                        <i className="ri-calendar-line me-1 text-muted"></i>
                        주간 예보
                    </div>
                </div>
                <div className="card-body d-flex align-items-center justify-content-center">
                    <p
                        className="text-muted fs-13 mb-0 d-flex align-items-center justify-content-center"
                        style={{ minHeight: 305 }}
                    >
                        주간 날씨 정보가 없습니다.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="card custom-card">
            <div className="card-header justify-content-between">
                <div className="card-title">
                    <i className="ri-calendar-line me-1 text-muted"></i>
                    주간 예보
                </div>
                <span className="badge bg-light text-muted fs-11">
                    {list.length}일
                </span>
            </div>
            <div className="card-body px-3 py-2">
                {list.map((w, i) => {
                    const isToday = w.wDate === nowDate;
                    const dateLabel = formatShortDate(w.wDate);
                    // dayOfWeek "월요일" → "월" 축약
                    const dayLabel = w.dayOfWeek?.slice(0, 1) ?? '';

                    return (
                        <div
                            key={i}
                            className="d-flex align-items-center py-2"
                            style={{
                                borderBottom:
                                    i < list.length - 1
                                        ? '1px solid var(--default-border)'
                                        : 'none',
                            }}
                        >
                            <span
                                className={`fw-semibold fs-13 ${isToday ? 'text-primary' : 'text-muted'}`}
                                style={{ width: 24 }}
                            >
                                {dayLabel}
                            </span>
                            <span
                                className="text-muted fs-12"
                                style={{ width: 38 }}
                            >
                                {dateLabel}
                            </span>
                            <img
                                src={getIcon(w.weatherAm)}
                                alt={w.weatherAm ?? ''}
                                style={{
                                    width: 24,
                                    height: 29.5,
                                    marginRight: 10,
                                }}
                            />
                            <span
                                className="text-muted flex-fill"
                                style={{ fontSize: 12 }}
                            >
                                <i className="ri-drop-line me-1"></i>
                                {w.rainRateAm ?? 0}%
                            </span>
                            <span className="text-danger fw-medium fs-12 me-3">
                                {w.maxTemp ?? '-'}°
                            </span>
                            <span className="text-muted fs-12">
                                {w.minTemp ?? '-'}°
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ────────────────────────────────────────────────────────────
// 메인 컴포넌트
// ────────────────────────────────────────────────────────────
const AgencyWeatherSection = () => {
    const [weather, setWeather] = useState<DashboardWeather | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.dashboardWeather()
            .then(data => setWeather(data))
            .catch(e => {
                console.error('날씨 조회 실패', e);
                setError(true);
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="row g-3">
                {/* 오늘 날씨 스켈레톤 */}
                <div className="col-12 col-md-8">
                    <div
                        className="card custom-card"
                        style={{ minHeight: 402 }}
                    >
                        <div className="card-header justify-content-between">
                            <div>
                                <div className="placeholder-glow">
                                    <span
                                        className="placeholder col-4 rounded mb-1"
                                        style={{ height: 16 }}
                                    />
                                    <span
                                        className="placeholder col-6 rounded d-block mb-1"
                                        style={{ height: 13 }}
                                    />
                                    <span
                                        className="placeholder col-8 rounded d-block"
                                        style={{ height: 13 }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            className="card-body d-flex flex-column align-items-center justify-content-center gap-3"
                            style={{ minHeight: 300 }}
                        >
                            {/* 스피너 */}
                            <div className="d-flex flex-column align-items-center gap-2">
                                <div
                                    className="spinner-border text-success"
                                    style={{ width: 36, height: 36 }}
                                    role="status"
                                />
                                <p className="mb-0 fw-semibold fs-14 text-muted">
                                    날씨 정보를 불러오는 중입니다...
                                </p>
                                <p
                                    className="mb-0 fs-12 text-muted text-center"
                                    style={{ maxWidth: 260 }}
                                >
                                    <i className="ri-information-line me-1 text-warning"></i>
                                    최초 날씨 정보 조회 시 다소 시간이 걸릴 수
                                    있습니다.
                                    <br />
                                    잠시만 기다려 주세요.
                                </p>
                            </div>

                            {/* 시간대별 스켈레톤 */}
                            <div className="w-100 placeholder-glow d-flex gap-2 mt-2">
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <span
                                        key={i}
                                        className="placeholder rounded-3 flex-fill"
                                        style={{ height: 80 }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 주간 예보 스켈레톤 */}
                <div className="col-12 col-md-4">
                    <div
                        className="card custom-card"
                        style={{ minHeight: 402 }}
                    >
                        <div className="card-header">
                            <div className="card-title placeholder-glow">
                                <span
                                    className="placeholder col-5 rounded"
                                    style={{ height: 16 }}
                                />
                            </div>
                        </div>
                        <div className="card-body px-3 py-2 placeholder-glow">
                            {Array.from({ length: 7 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="d-flex align-items-center py-2 gap-2"
                                    style={{
                                        borderBottom:
                                            i < 6
                                                ? '1px solid var(--default-border)'
                                                : 'none',
                                    }}
                                >
                                    <span
                                        className="placeholder rounded"
                                        style={{ width: 20, height: 14 }}
                                    />
                                    <span
                                        className="placeholder rounded"
                                        style={{ width: 32, height: 14 }}
                                    />
                                    <span
                                        className="placeholder rounded"
                                        style={{ width: 24, height: 24 }}
                                    />
                                    <span
                                        className="placeholder rounded flex-fill"
                                        style={{ height: 14 }}
                                    />
                                    <span
                                        className="placeholder rounded"
                                        style={{ width: 28, height: 14 }}
                                    />
                                    <span
                                        className="placeholder rounded"
                                        style={{ width: 28, height: 14 }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !weather) {
        return (
            <div className="alert alert-warning fs-13">
                <i className="ri-error-warning-line me-1"></i>
                날씨 정보를 불러오지 못했습니다.
            </div>
        );
    }

    return (
        <div className="row g-3">
            <div className="col-12 col-md-8">
                <TodayWeatherCard data={weather} />
            </div>
            <div className="col-12 col-md-4">
                <WeeklyForecastCard
                    list={weather.list ?? []}
                    nowDate={weather.nowDate}
                />
            </div>
        </div>
    );
};

export default AgencyWeatherSection;
